// Ingredientes Page
import React, { useEffect, useMemo, useState } from 'react';
import { ingredientesAPI, proveedoresAPI, alergenosAPI, etiquetasAPI, getAuthToken } from '../api/api';
import { Plus, Search, Edit, Trash2, Package, CheckCircle2, FileText } from 'lucide-react';
import IngredienteModal, { textoSeccionCompra } from '../components/ingredientes/IngredienteModal';
import { Toaster } from '../components/ui/toaster';
import {
  ABREVIATURA_FUENTE, NOMBRE_CONSERVACION, NOMBRE_FUENTE, claveFuente, esHastaEnvase, infoFuente, textoVida,
} from '../utils/etiquetas';

// Cómo se conserva un ingrediente una vez abierto (configuración de Etiquetas)
const CLASE_CONSERVACION = {
  seco: 'bg-gray-100 text-gray-700',
  refrigerado: 'bg-sky-100 text-sky-800',
  congelado: 'bg-gray-900 text-white',
};
const BORDE_CONFIRMADO = {
  seco: 'border border-gray-200',
  refrigerado: 'border border-sky-200',
  congelado: 'border border-gray-900',
};

const PildoraAbierto = ({ config }) => {
  if (!config) return <span className="text-gray-400">—</span>;
  const clase = CLASE_CONSERVACION[config.conservacion] || CLASE_CONSERVACION.seco;
  const borde = config.confirmado
    ? (BORDE_CONFIRMADO[config.conservacion] || BORDE_CONFIRMADO.seco)
    : 'border-2 border-dashed border-amber-400';
  const vida = esHastaEnvase(config) ? 'Hasta envase' : textoVida(config);
  // Fuente del plazo en letra pequeña (la propuesta ya se ve por el borde discontinuo)
  const fuente = claveFuente(config);
  const info = infoFuente(config);
  return (
    <div>
      <span
        className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${clase} ${borde}`}
        title={config.confirmado ? undefined : 'Propuesta sin confirmar'}
      >
        {NOMBRE_CONSERVACION[config.conservacion] || config.conservacion}
      </span>
      {vida && <div className="text-[11px] text-gray-500 mt-0.5">{vida}</div>}
      {fuente !== 'propuesta' && (
        <div
          className="text-[10px] text-gray-400 leading-tight"
          title={info.detalle ? `${NOMBRE_FUENTE[fuente]}: ${info.detalle}` : NOMBRE_FUENTE[fuente]}
          data-testid="ingrediente-abierto-fuente"
        >
          {ABREVIATURA_FUENTE[fuente]}
        </div>
      )}
    </div>
  );
};

// Función para normalizar texto (quitar acentos) para búsquedas
const normalizeText = (text) => {
  if (!text) return '';
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
};

const Ingredientes = () => {
  const [ingredientes, setIngredientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [alergenos, setAlergenos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProveedor, setFilterProveedor] = useState('');
  const [filterActivo, setFilterActivo] = useState('true');
  const [filterAbierto, setFilterAbierto] = useState('');
  // Respuesta de etiquetasAPI.productos('ingrediente') o null si no se pudo cargar
  const [etiquetas, setEtiquetas] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingIngrediente, setEditingIngrediente] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ingredientesRes, proveedoresRes, alergenosRes, etiquetasRes] = await Promise.all([
        ingredientesAPI.getAll(),
        proveedoresAPI.getAll(),
        alergenosAPI.getAll(),
        // Si falla, la página funciona igual y la columna "Abierto" enseña "—"
        etiquetasAPI.productos('ingrediente').catch((error) => {
          console.error('Error cargando la conservación de etiquetas:', error);
          return null;
        }),
      ]);

      // Ordenar ingredientes alfabéticamente por nombre
      const ingredientesOrdenados = Array.isArray(ingredientesRes.data)
        ? ingredientesRes.data.sort((a, b) => a.nombre.localeCompare(b.nombre))
        : [];

      setIngredientes(ingredientesOrdenados);
      setProveedores(Array.isArray(proveedoresRes.data) ? proveedoresRes.data : []);
      setAlergenos(Array.isArray(alergenosRes.data) ? alergenosRes.data : []);
      setEtiquetas(etiquetasRes && Array.isArray(etiquetasRes.data?.productos) ? etiquetasRes.data : null);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este ingrediente?')) {
      try {
        await ingredientesAPI.delete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting ingrediente:', error);
      }
    }
  };

  // La ficha (campos, validaciones y guardado) vive en IngredienteModal
  const openEditModal = (ingrediente) => {
    setEditingIngrediente(ingrediente);
    setShowModal(true);
  };

  const closeModal = () => {
    setEditingIngrediente(null);
    setShowModal(false);
  };

  const getProveedorName = (proveedorId) => {
    const proveedor = proveedores.find(p => p.id === proveedorId);
    return proveedor?.nombre || '-';
  };

  // Toggle revisado status
  const toggleRevisado = async (ingredienteId, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ingredientes/${ingredienteId}/toggle-revisado`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Actualizar el estado local
        setIngredientes(prev => prev.map(ing => 
          ing.id === ingredienteId ? { ...ing, revisado: data.revisado } : ing
        ));
      }
    } catch (error) {
      console.error('Error toggling revisado:', error);
    }
  };

  // Configuración de etiquetas por id de ingrediente (los inactivos no salen)
  const etiquetaPorId = useMemo(() => {
    const mapa = new Map();
    (etiquetas?.productos || []).forEach((p) => {
      if (p.tipo === 'ingrediente' && p.id) mapa.set(p.id, p);
    });
    return mapa;
  }, [etiquetas]);

  const filteredIngredientes = ingredientes.filter(ing => {
    const matchesSearch = normalizeText(ing.nombre).includes(normalizeText(searchTerm));
    const matchesProveedor = !filterProveedor || ing.proveedor_id === filterProveedor;
    const matchesActivo = filterActivo === '' || ing.activo.toString() === filterActivo;
    const config = etiquetaPorId.get(ing.id);
    const matchesAbierto = !filterAbierto || (!!config && (filterAbierto === 'sin_confirmar'
      ? !config.confirmado
      : config.conservacion === filterAbierto));
    // Mostrar todos los ingredientes (sin filtro de categoría)
    return matchesSearch && matchesProveedor && matchesActivo && matchesAbierto;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64">Cargando...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="ingredientes-title">Ingredientes</h1>
          <p className="text-gray-600 mt-1">Gestión de ingredientes y materias primas</p>
          {/* Contador de revisados */}
          <div className="flex items-center gap-4 mt-2">
            <span className="text-sm text-gray-500">
              <span className="font-semibold text-green-600">{filteredIngredientes.filter(i => i.revisado).length}</span>
              <span> / {filteredIngredientes.length} revisados</span>
            </span>
            {filteredIngredientes.length > 0 && (
              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all duration-300"
                  style={{ width: `${(filteredIngredientes.filter(i => i.revisado).length / filteredIngredientes.length) * 100}%` }}
                />
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', `${process.env.REACT_APP_BACKEND_URL}/api/ingredientes/generar-listado-pdf`, true);
            xhr.setRequestHeader('Authorization', `Bearer ${getAuthToken()}`);
            xhr.responseType = 'blob';
            
            xhr.onload = function() {
              if (xhr.status === 200) {
                const blob = xhr.response;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Listado_Ingredientes_${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } else {
                alert('Error al generar el PDF del listado');
              }
            };
            
            xhr.onerror = function() {
              alert('Error al generar el PDF del listado');
            };
            
            xhr.send();
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition-colors"
        >
          <FileText className="w-5 h-5" />
          <span>Descargar Listado PDF</span>
        </button>
        <button
          onClick={() => setShowModal(true)}
          data-testid="new-ingredient-button"
          className="bg-duetto-primary text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-opacity-90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Nuevo Ingrediente</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar ingrediente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
          </div>
          <select
            value={filterProveedor}
            onChange={(e) => setFilterProveedor(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          >
            <option value="">Todos los proveedores</option>
            {[...proveedores].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(prov => (
              <option key={prov.id} value={prov.id}>{prov.nombre}</option>
            ))}
          </select>
          <select
            value={filterActivo}
            onChange={(e) => setFilterActivo(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          >
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
          <select
            value={filterAbierto}
            onChange={(e) => setFilterAbierto(e.target.value)}
            disabled={!etiquetas}
            title={etiquetas ? 'Cómo se conserva una vez abierto' : 'No se pudo cargar cómo se conserva cada ingrediente abierto'}
            data-testid="filtro-abierto"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary disabled:opacity-60"
          >
            <option value="">Abierto: todas</option>
            <option value="seco">Abierto: Seco</option>
            <option value="refrigerado">Abierto: Refrigerado</option>
            <option value="congelado">Abierto: Congelado</option>
            <option value="sin_confirmar">Abierto: Sin confirmar</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        <table className="w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-10">✓</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase max-w-[180px]">Ingrediente</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase" title="Cómo se conserva una vez abierto">Abierto</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">€/UD</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">€/Envase</th>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredIngredientes.map((ingrediente) => {
              const ingredienteAlergenos = (ingrediente.alergenos_ids || [])
                .map(id => alergenos.find(a => a.id === id))
                .filter(Boolean);
              
              return (
                <tr 
                  key={ingrediente.id} 
                  onClick={() => openEditModal(ingrediente)}
                  className={`hover:bg-gray-50 cursor-pointer transition-colors ${ingrediente.revisado ? 'bg-green-50' : ''}`}
                >
                  {/* Checkbox Revisado */}
                  <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => toggleRevisado(ingrediente.id, e)}
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                        ingrediente.revisado 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'border-gray-300 hover:border-green-400'
                      }`}
                      title={ingrediente.revisado ? 'Marcar como no revisado' : 'Marcar como revisado'}
                    >
                      {ingrediente.revisado && <CheckCircle2 className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 bg-duetto-primary bg-opacity-10 rounded flex items-center justify-center">
                        <Package className="w-4 h-4 text-duetto-primary" />
                      </div>
                      <div className="ml-2">
                        <div className="font-medium text-gray-900">{ingrediente.nombre}</div>
                        <div className="text-xs text-gray-500">
                          {ingrediente.unidad}
                          {ingrediente.seccion_compra ? ` · ${textoSeccionCompra(ingrediente.seccion_compra) || ingrediente.seccion_compra}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700 max-w-[120px] truncate" title={getProveedorName(ingrediente.proveedor_id)}>
                    {getProveedorName(ingrediente.proveedor_id)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap" data-testid="ingrediente-abierto">
                    <PildoraAbierto config={etiquetaPorId.get(ingrediente.id)} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <div className="font-medium text-gray-900">{ingrediente.precio_unitario?.toFixed(2) || '0.00'}€</div>
                    <div className="text-xs text-gray-400">
                      (+IVA: {(ingrediente.precio_unitario * (ingrediente.tipo_iva || 10) / 100)?.toFixed(2) || '0.00'}€)
                    </div>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-right">
                    <div className="font-medium text-gray-900">{ingrediente.precio_paquete?.toFixed(2) || '0.00'}€</div>
                    <div className="text-xs text-gray-400">{ingrediente.unidades_por_paquete} {ingrediente.unidad || 'uds'}</div>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-center">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      ingrediente.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {ingrediente.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(ingrediente);
                      }}
                      className="text-duetto-primary hover:text-duetto-secondary mr-2 p-1"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(ingrediente.id);
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredIngredientes.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron ingredientes
          </div>
        )}
      </div>

      {/* Ficha del ingrediente (modal compartido con la lista de compra) */}
      <IngredienteModal
        abierto={showModal}
        ingrediente={editingIngrediente}
        proveedores={proveedores}
        alergenos={alergenos}
        etiquetas={etiquetas}
        onProveedoresCambiados={fetchData}
        onGuardado={fetchData}
        onCerrar={closeModal}
      />
      <Toaster />
    </div>
  );
};

export default Ingredientes;
